/** @shell standard @header bar */
import React, { useMemo, useCallback, useState } from 'react';
import { View, Text, StyleSheet, Share, RefreshControl, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { SolidCtaClickable } from '../components/common/SolidCtaClickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  androidScaleFont,
} from '../shared/theme/studio/stackScreenShell';
import { useAuthStore } from '../shared/auth/store';
import { fetchProfileCustomer } from '../data/domains/profile';
import { usePointsHistory, useLoyalty } from '../data/domains/loyalty';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { triggerHaptic } from '../shared/utils/haptics';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireLoyaltyEnabled } from '../shared/navigation/useFeatureGateExit';
import moment from 'moment';

const profileQueryKey = ['profile'] as const;

function createLoyaltyStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  return StyleSheet.create({
    contentWrap: { flex: 1 },
    scroll: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    heroCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.xl,
      alignItems: 'center',
      backgroundColor: c.primary,
    },
    heroEyebrow: {
      ...appTheme.typography.captionSmall,
      fontSize: sf(11),
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: hexToRgba(c.onPrimary ?? '#fff', 0.82),
    },
    heroMetric: {
      fontSize: sf(44),
      letterSpacing: -1.2,
      fontWeight: '800',
      color: c.onPrimary ?? c.white ?? '#FFFFFF',
      marginTop: appTheme.spacing.sm,
    },
    heroTier: {
      marginTop: appTheme.spacing.sm,
      fontSize: sf(15),
      fontWeight: '700',
      color: hexToRgba(c.onPrimary ?? '#fff', 0.92),
    },
    heroSubline: {
      marginTop: appTheme.spacing.xs,
      fontSize: sf(13),
      fontWeight: '600',
      color: hexToRgba(c.onPrimary ?? '#fff', 0.82),
      textAlign: 'center',
    },
    panel: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.lg,
      backgroundColor: c.surface,
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    panelTitle: {
      ...appTheme.typography.body,
      fontSize: 17,
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    panelSub: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.md,
    },
    codeShareRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: appTheme.spacing.sm,
    },
    codeBox: {
      flex: 1,
      minWidth: 0,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      backgroundColor: hexToRgba(c.primary, 0.06),
      borderRadius: DASH_STUDIO_RADIUS,
    },
    codeText: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.text,
      letterSpacing: 1.5,
      textAlign: 'center',
    },
    pointsList: {
      marginHorizontal: -appTheme.spacing.md,
      marginBottom: -appTheme.spacing.md,
    },
    pointsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    pointsRowLast: {
      borderBottomWidth: 0,
    },
    pointsRowIcon: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: `${c.primary}18`,
      justifyContent: 'center',
      alignItems: 'center',
      marginEnd: appTheme.spacing.md,
    },
    pointsRowContent: {
      flex: 1,
      minWidth: 0,
    },
    pointsRowTitle: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
    },
    pointsRowDate: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      marginTop: 2,
    },
    pointsRowPts: {
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.primary,
      marginStart: appTheme.spacing.sm,
    },
    emptyWrap: {
      paddingVertical: appTheme.spacing.xl * 1.25,
      paddingHorizontal: appTheme.spacing.md,
      alignItems: 'center',
    },
    emptyIconCircle: {
      width: 72,
      height: 72,
      borderRadius: 36,
      backgroundColor: c.primaryUltra,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.md,
    },
    emptyText: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
    },
    emptySub: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: appTheme.spacing.xs,
      textAlign: 'center',
    },
    contentLoader: {
      flex: 1,
      paddingTop: appTheme.spacing.lg,
    },
  });
}

function useProfileForLoyalty() {
  const customer_id = useAuthStore(s => s.user?.id);
  const query = useQuery({
    queryKey: [...profileQueryKey, customer_id],
    queryFn: async () => fetchProfileCustomer(customer_id!),
    enabled: typeof customer_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
  return {
    profile: query.data,
    available_loyalty_points: Number(query.data?.available_loyalty_points ?? 0),
    code: query.data?.code ?? '',
    isLoading: query.isLoading,
    refetch: query.refetch,
    error: query.error,
  };
}

function PointsRow({
  item,
  styles,
  primaryColor,
  isLast,
}: {
  item: { description: string; points: number; date: string };
  styles: ReturnType<typeof createLoyaltyStyles>;
  primaryColor: string;
  isLast: boolean;
}) {
  return (
    <View style={[styles.pointsRow, isLast && styles.pointsRowLast]}>
      <View style={styles.pointsRowIcon}>
        <SvgIcon name="receipt" size={22} color={primaryColor} />
      </View>
      <View style={styles.pointsRowContent}>
        <Text style={styles.pointsRowTitle} numberOfLines={2}>
          {item.description}
        </Text>
        <Text style={styles.pointsRowDate}>{item.date ? moment(item.date).format('MMM D, YYYY') : '—'}</Text>
      </View>
      <Text style={styles.pointsRowPts}>+{item.points}</Text>
    </View>
  );
}

export const LoyaltyScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const loyalty_enabled = useRequireLoyaltyEnabled();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const content_top = androidAppHeaderContentTop(insets.top, appTheme.spacing.sm);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const user = useAuthStore(s => s.user);
  const {
    available_loyalty_points,
    code: profile_code,
    isLoading: loadingProfile,
    refetch: refetchProfile,
    error: profileError,
  } = useProfileForLoyalty();
  const { data: loyaltyPointsHistory = [], isLoading: loadingHistory } = usePointsHistory();
  const { data: loyaltySummary, isLoading: loadingLoyalty, refetch: refetchLoyalty } = useLoyalty();

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createLoyaltyStyles(colors), [colors]);

  const points_list = useMemo(() => {
    const raw = Array.isArray(loyaltyPointsHistory) ? loyaltyPointsHistory : [];
    return raw
      .filter((p: { points?: number; date?: string }) => (p.points ?? 0) > 0 || p.date)
      .sort((a: { date?: string }, b: { date?: string }) => (b.date && a.date ? (b.date > a.date ? 1 : -1) : 0))
      .map((item: { id: number | string; description?: string; points: number; date?: string }) => ({
        id: String(item.id),
        description: item.description ?? t('loyalty.pointsEarned'),
        points: item.points,
        date: item.date ?? '',
      }));
  }, [loyaltyPointsHistory, t]);

  const member_code = profile_code || (user as any)?.code || '';
  const tier_label = loyaltySummary?.tier ? String(loyaltySummary.tier) : '';

  const [refreshing, set_refreshing] = useState(false);
  const on_refresh = useCallback(async () => {
    triggerHaptic('impactLight');
    set_refreshing(true);
    await Promise.all([refetchProfile(), refetchLoyalty()]);
    set_refreshing(false);
  }, [refetchProfile, refetchLoyalty]);

  const handle_share = useCallback(async () => {
    triggerHaptic('impactMedium');
    try {
      await Share.share({
        message: t('loyalty.referralMessage').replace('{{code}}', member_code),
        title: t('loyalty.referralTitle'),
      });
    } catch (_) {}
  }, [member_code, t]);

  const isLoading = loadingProfile || loadingHistory || loadingLoyalty;

  if (!loyalty_enabled) {
    return null;
  }

  if (isLoading && !points_list.length && !member_code) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader title={t('loyalty.title')} onBack={on_back_exit} scrollY={scrollY} />
        <View style={[styles.contentLoader, { paddingTop: content_top }]}>
          <LoadingState />
        </View>
      </StackScreenShell>
    );
  }
  if (profileError && !points_list.length) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader title={t('loyalty.title')} onBack={on_back_exit} scrollY={scrollY} />
        <View style={{ paddingTop: content_top, flex: 1 }}>
          <ErrorState
            title={t('loyalty.couldntLoad')}
            message={t('loyalty.failedToLoad')}
            onRetry={() => {
              void refetchProfile();
              void refetchLoyalty();
            }}
            error={profileError}
            issue_context={{
              page: t('loyalty.title'),
              issue: 'Failed to load loyalty profile or points',
              kind: 'api',
              api_endpoint: 'profile / loyalty',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={t('loyalty.title')} onBack={on_back_exit} scrollY={scrollY} />

      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: content_top,
            paddingBottom: Math.max(insets.bottom, appTheme.spacing.lg) + appTheme.spacing.xl,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={on_refresh}
            tintColor={colors.primary}
            colors={Platform.OS === 'android' ? [colors.primary] : undefined}
            progressBackgroundColor={Platform.OS === 'android' ? colors.surface : undefined}
          />
        }
        showsVerticalScrollIndicator={false}>
        <View style={styles.heroCard}>
          <Text style={styles.heroEyebrow}>{t('loyalty.availablePoints')}</Text>
          <Text style={styles.heroMetric}>{available_loyalty_points.toLocaleString()}</Text>
          {tier_label ? (
            <Text style={styles.heroTier} numberOfLines={1}>
              {tier_label}
            </Text>
          ) : null}
          <Text style={styles.heroSubline}>{t('loyalty.useAtCheckout')}</Text>
        </View>

        {member_code ? (
          <View style={styles.panel}>
            <View style={styles.panelHeader}>
              <SvgIcon name="users" size={22} color={colors.primary} />
              <Text style={styles.panelTitle}>{t('loyalty.referAndEarn')}</Text>
            </View>
            <Text style={styles.panelSub}>{t('loyalty.referralSub')}</Text>
            <View style={styles.codeShareRow}>
              <View style={styles.codeBox}>
                <Text style={styles.codeText} selectable>
                  {member_code}
                </Text>
              </View>
              <SolidCtaClickable
                label={t('loyalty.share')}
                onPress={handle_share}
                variant="primary"
                size="compact"
                widthMode="hug"
                haptics={true}
                accessibilityLabel={t('loyalty.share')}
              />
            </View>
          </View>
        ) : null}

        <View style={styles.panel}>
          <View style={styles.panelHeader}>
            <SvgIcon name="receipt" size={22} color={colors.primary} />
            <Text style={styles.panelTitle}>{t('loyalty.pointsHistory')}</Text>
          </View>
          {points_list.length === 0 ? (
            <View style={styles.emptyWrap}>
              <View style={styles.emptyIconCircle}>
                <SvgIcon name="receipt" size={32} color={colors.primary} />
              </View>
              <Text style={styles.emptyText}>{t('loyalty.noPointsHistory')}</Text>
              <Text style={styles.emptySub}>{t('loyalty.earnPointsSub')}</Text>
            </View>
          ) : (
            <View style={styles.pointsList}>
              {points_list.map((item, index) => (
                <PointsRow
                  key={item.id}
                  item={item}
                  styles={styles}
                  primaryColor={colors.primary}
                  isLast={index === points_list.length - 1}
                />
              ))}
            </View>
          )}
        </View>
      </Animated.ScrollView>
    </StackScreenShell>
  );
};
